// Shared shape for a new users/{uid} document.
// Email/password sign-up and Google sign-up both use this so the fields and
// defaults stay the same. The client never sets role (admin is not granted
// here). The blocked keys are the ones a tightened users create rule rejects
// (see the open rules PR): role, scores, and Stripe ids. verified stays false
// until an admin approves a carrier.

import { serverTimestamp } from 'firebase/firestore';

// Keys a client must not send on create. `role` is included so a caller cannot
// set role to admin (or to anything else). Score and Stripe fields are pinned
// to the defaults below instead of whatever extra contains.
const BLOCKED_ON_CREATE = new Set([
  'role',
  'earlyAdopter',
  'fiveStarReviews',
  'leagueWins',
  'weeklyRivalWins',
  'referralCredits',
  'referralCount',
  'firstBids',
  'totalBids',
  'currentStreak',
  'lastJobDate',
  'complaintsReceived',
  'stripeAccountId',
  'stripeOnboardingComplete',
  'type',
  'credits',
  'rating',
  'jobs',
  'verified',
  'email',
  'name',
  'createdAt',
  'ecoRate',
  'rank',
]);

export function buildNewUserProfile({ name, email, type, extra = {} }) {
  const safeExtra = {};
  Object.entries(extra).forEach(([key, value]) => {
    if (!BLOCKED_ON_CREATE.has(key)) safeExtra[key] = value;
  });

  return {
    name,
    email,
    type, // 'customer' or 'provider'
    credits: type === 'provider' ? 0 : null,
    rating: type === 'provider' ? 0 : null,
    jobs: 0,
    ecoRate: type === 'provider' ? 0 : null,
    verified: false,
    rank: null,
    createdAt: serverTimestamp(),
    ...safeExtra,
  };
}

// Decide what a signed-in Firebase user means for the app.
// A Google user with no users/{uid} doc still has to finish account setup.
// keptProfile covers the gap where auth state resolves before the new doc
// write is visible.
export function classifySignedInUser({ firebaseUser, profile, keptProfile }) {
  const emailVerified = !!firebaseUser?.emailVerified;
  const email = firebaseUser?.email || '';
  const uid = firebaseUser?.uid;

  if (profile) {
    return {
      kind: 'existing',
      user: { uid, email, ...profile, emailVerified },
    };
  }

  if (keptProfile && keptProfile.uid === uid) {
    return {
      kind: 'existing',
      user: { ...keptProfile, email, emailVerified },
    };
  }

  const providers = firebaseUser?.providerData || [];
  const google = providers.some((entry) => entry?.providerId === 'google.com');
  if (google) {
    if (!email) return { kind: 'missingEmail' };
    return {
      kind: 'needsSetup',
      pending: {
        uid,
        email,
        name: firebaseUser.displayName || '',
        emailVerified,
      },
    };
  }

  return {
    kind: 'partial',
    user: { uid, email, emailVerified },
  };
}
